import {
  Component,
  OnInit,
  ElementRef,
  AfterViewInit,
  ChangeDetectionStrategy,
  DestroyRef,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { ImageSlide } from './models/image-slide';
import { Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { Button } from '@openng/optimus-ui/button';
import { Ag1LoaderComponent } from '../ag1-loader/ag1-loader.component';
import { GalleriaModule } from '@openng/optimus-ui/galleria';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'ag-image-slider',
  templateUrl: './image-slider.component.html',
  host: {
    class: 'block w-full',
    '(document:keydown)': 'onGalleryKeydown($event)',
  },
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [AngularSvgIconModule, Button, Ag1LoaderComponent, GalleriaModule],
})
export class ImageSliderComponent implements OnInit, AfterViewInit {
  private _destroyRef = inject(DestroyRef);
  readonly images = input<Array<ImageSlide>>(new Array<ImageSlide>());

  currentImage: any;
  galleryIsOpen = false;
  galleryIndex = 0;
  loadingImage = true;
  currentImageSubject: Subject<ImageSlide> = new Subject();

  readonly currentImageRef =
    viewChild<ElementRef<HTMLImageElement>>('currentImageRef');

  constructor() {}

  ngOnInit(): void {
    this.currentImageSubject
      .pipe(
        tap((_) => {
          this.loadingImage = true;
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((image) => {
        this.currentImage = image;
      });

    this.currentImageSubject.next(this.images()[0]);
  }

  ngAfterViewInit(): void {
    this.currentImageRef().nativeElement.onload = () => {
      this.loadingImage = false;
    };
  }

  // Open the gallery on the image the slider is showing.
  showGallery() {
    this.galleryIndex = Math.max(
      this.images().findIndex((image) => image.id === this.currentImage?.id),
      0,
    );
    this.galleryIsOpen = true;
  }

  // Galleria does not handle Escape or arrow keys for the main image.
  onGalleryKeydown(event: KeyboardEvent) {
    if (!this.galleryIsOpen) {
      return;
    }

    const count = this.images().length;

    switch (event.key) {
      case 'Escape':
        this.galleryIsOpen = false;
        break;
      case 'ArrowRight':
        this.galleryIndex = (this.galleryIndex + 1) % count;
        break;
      case 'ArrowLeft':
        this.galleryIndex = (this.galleryIndex - 1 + count) % count;
        break;
    }
  }

  nextImage() {
    const currentIndex = this.images().findIndex(
      (v) => v.id === this.currentImage.id,
    );
    if (currentIndex + 1 > this.images().length - 1) {
      this.currentImageSubject.next(this.images()[0]);
    } else {
      this.currentImageSubject.next(this.images()[currentIndex + 1]);
    }
  }

  previousImage() {
    const currentIndex = this.images().findIndex(
      (v) => v.id === this.currentImage.id,
    );
    if (currentIndex - 1 === -1) {
      this.currentImageSubject.next(this.images()[this.images().length - 1]);
    } else {
      this.currentImageSubject.next(this.images()[currentIndex - 1]);
    }
  }
}
